import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, createTextVNode as _createTextVNode, createVNode as _createVNode } from "vue"
import _imports_0 from '../static/icons/trash_icon.svg'


const _hoisted_1 = { class: "cart-group-item" }
const _hoisted_2 = { class: "cart-group-item__toolbar" }
const _hoisted_3 = { class: "cart-group-item__title" }
const _hoisted_4 = {
  key: 0,
  class: "cart-group-item__decorated"
}
const _hoisted_5 = { class: "cart-group-item__rows" }
const _hoisted_6 = { class: "cart-group-item__remove-group" }
const _hoisted_7 = { class: "cart-group-item__pricing" }
const _hoisted_8 = { class: "cart-group-item__subtotal" }
const _hoisted_9 = { class: "cart-group-item__subtotal-price" }
const _hoisted_10 = {
  key: 0,
  class: "cart-group-item__pricing-content"
}
const _hoisted_11 = { class: "cart-group-item__price-items" }
const _hoisted_12 = { class: "cart-group-item__price-items-title" }
const _hoisted_13 = {
  key: 0,
  class: "cart-group-item__price-items"
}
const _hoisted_14 = { class: "cart-group-item__price-items-title" }
const _hoisted_15 = {
  key: 1,
  class: "cart-group-item__price-items"
}
const _hoisted_16 = { class: "cart-group-item__price-items-title" }
const _hoisted_17 = { class: "cart-group-item__upsell-items" }
const _hoisted_18 = {
  key: 2,
  class: "cart-group-item__price-items"
}
const _hoisted_19 = { class: "cart-group-item__price-items-title" }

import { ref, computed, inject } from 'vue';
import { fetchPageBuilder, invokeKey } from '@drapejs/core';
import { useCartFunctions } from '@/helpers/cart-actions';
import Checkbox from './forms/Checkbox.vue';
import CartItem from './CartItem.vue';
import useContext from '@/composables/useContext';
import CartPriceItem from './CartPriceItem.vue';
import ConfiguratorChevron from './ProductConfigurator/ConfiguratorChevron.vue';
import * as vingaSales from '../../connectors/litium/sales.ts';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import { getAddonTitle } from '@/helpers/printess';


export default /*@__PURE__*/_defineComponent({
  __name: 'CartGroupItem',
  props: {
  group: {
    type: Object,
    required: true
  }
},
  emits: ['edit'],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;
const invoke = inject(invokeKey, async () => null);
const { websiteText } = useWebsiteTexts();
const { formatPrice, route, isAuthorized, navigateToLoginPage } = useContext();
const { removeGroupFromCart } = useCartFunctions();
const isPriceExpanded = ref(false);

const productName = computed(() => props.group.baseProductName || '');
const hasPrintRow = computed(() =>
  props.group.rows.some((r) => r.printRow?.unitPrice)
);
const hasGiftCardAddons = computed(() =>
  props.group.rows.some(
    (r) => r.giftCardInfo && r.giftCardInfo?.addons?.length > 0
  )
);
const hasGiftCardShipping = computed(() =>
  props.group.rows.some(
    (r) => r.giftCardInfo && r.giftCardInfo?.giftCardShipping !== null
  )
);
const upsellItems = computed(() => {
  return props.group.rows[0].upsellItems;
});

const mappedProducts = computed(() => {
  return (
    props.group.rows.map((row) => {
      const name = getRowName(row);
      const originalPrice = getOriginalPrice(row);
      const currentPrice = getCurrentPrice(row);
      const campaignPercentage = getCampaignPercentage(row);
      return {
        name,
        id: row.id,
        quantity: row.quantity,
        currentPrice,
        originalPrice,
        campaignPercentage,
        addons: row.addOns,
        articleNumber: row.articleNumber,
        articleNumberForDisplay: row.articleNumberForDisplay
      };
    }) || []
  );
});

const mappedDecorations = computed(() => {
  if (!hasPrintRow.value) return [];
  const print = props.group.rows[0].printRow;
  const addons = props.group.rows[0].addOns.filter((a) => a.unitPrice);
  const quantity =
    mappedProducts.value.reduce((acc, curr) => acc + curr.quantity, 0) || 1;

  return [
    {
      unitPrice: print.unitPrice,
      quantity,
      name: print.name || '',
      addons,
      id: print.systemId
    }
  ];
});

const decorationPriceSum = computed(() => {
  if (!hasPrintRow.value) return 0;
  const decoration = mappedDecorations.value[0];
  const decorationUnitsTotal = decoration.unitPrice * decoration.quantity;
  return (
    decorationUnitsTotal +
    decoration.addons.reduce((acc, a) => acc + a.unitPrice, 0)
  );
});

const showEditButton = computed(() => {
  return props.group.rows.every((r) => r.giftCardInfo === null);
});

const mappedGiftCardAddons = computed(() => {
  if (!hasGiftCardAddons.value) return [];
  const addons = props.group.rows[0].giftCardInfo?.addons;
  return addons.map((a) => {
    return {
      unitPrice: a.price,
      quantity: a.quantity,
      name: getAddonTitle(a) || '',
      id: a.id
    };
  });
});

const giftcardShippingPriceSum = computed(() => {
  if (!hasGiftCardShipping.value) return 0;
  return mappedGiftCardShipping.value.reduce(
    (acc, a) => acc + a.unitPrice * a.quantity,
    0
  );
});

const mappedGiftCardShipping = computed(() => {
  if (!hasGiftCardShipping.value) return [];
  const shipping = props.group.rows[0].giftCardInfo?.giftCardShipping;
  return [
    {
      unitPrice: shipping.price,
      quantity: shipping.quantity,
      name: websiteText('giftcard__shipping').value,
      id: shipping.id || 'PKFRAKT'
    }
  ];
});

const giftcardAddonsPriceSum = computed(() => {
  if (!hasGiftCardAddons.value) return 0;
  return mappedGiftCardAddons.value.reduce(
    (acc, a) => acc + a.unitPrice * a.quantity,
    0
  );
});

const productPriceSum = computed(() => {
  return mappedProducts.value.reduce((acc, r) => {
    const rowSum = r.currentPrice * r.quantity;
    return acc + rowSum;
  }, 0);
});

const groupPriceSum = computed(() => {
  const activeUpsell = upsellItems.value.reduce((acc, u) => {
    return u.isActive ? acc + u.value : acc;
  }, 0);
  return (
    decorationPriceSum.value +
    giftcardAddonsPriceSum.value +
    productPriceSum.value +
    giftcardShippingPriceSum.value +
    activeUpsell
  );
});

function getRowName(row) {
  let variant = '';
  if (row.color && row.size) {
    variant += row.size ? `${row.color}, ${row.size}` : row.color;
  } else if (row.size) {
    variant += row.size || '';
  } else {
    variant = row.variantName;
  }
  return variant;
}

function getCurrentPrice(row) {
  return row.unitPrice ?? row.unitListPrice;
}

function getOriginalPrice(row) {
  return row.originalPrice >= getCurrentPrice(row)
    ? row.originalPrice
    : getCurrentPrice(row);
}

function getCampaignPercentage(row) {
  const current = getCurrentPrice(row);
  const original = getOriginalPrice(row);

  if (current < original) {
    const percentage = Math.abs(((original - current) / original) * -100);
    if (percentage > 1) {
      return Number(formatPrice(percentage, undefined, '', '', 'decimal'));
    }
  }

  return 0;
}

function getUpsellItemText(upsellItem) {
  const text =
    upsellItem.text?.charAt(0).toLowerCase() + upsellItem.text?.slice(1);
  const variantName = `${text} (${formatPrice(upsellItem.value)})`;

  return websiteText('global__add_upsell', { variant: variantName }).value;
}

function getUpsellItemPrice(upsellItem) {
  if (upsellItem.isActive) {
    return formatPrice(upsellItem.value);
  }
  return '';
}

async function removeProduct() {
  if (!isAuthorized()) {
    navigateToLoginPage();
    return;
  }

  const { _, error } = await removeGroupFromCart(
    props.group.groupId,
    props.group.rowId
  );
}

function editIfAuthorized(group) {
  if (!isAuthorized()) {
    navigateToLoginPage();
    return;
  }

  emit('edit', {
    id: group.groupId,
    url: group.baseProductUrl,
    name: productName,
    articleNumber: group.articleNumber
  });
}

function buildRequest() {
  return fetchPageBuilder(
    route.protocol,
    route.host,
    route.pathname,
    {
      ...route.query
    },
    ''
  );
}

async function toggleUpsellItem(upsellItem) {
  if (!isAuthorized()) {
    navigateToLoginPage();
    return;
  }

  const request = buildRequest();
  request.rowSystemId = props.group.rowId;
  request.variantSystemId = props.group.variantSystemId;
  request.upsellItemRowSystemId = upsellItem.rowSystemId;
  request.upsellItemVariantSystemId = upsellItem.variantSystemId;
  request.isUpsellItem = true;
  request.quantity = upsellItem.isActive ? 1 : 0;

  await invoke(vingaSales.commands.setCartRowUpsellProduct, request);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", null, [
        _createElementVNode("div", _hoisted_3, _toDisplayString(productName.value), 1),
        (hasPrintRow.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_unref(websiteText)('decoration__decorated').value), 1))
          : _createCommentVNode("", true)
      ]),
      (showEditButton.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: "cart-group-item__edit secondary-button",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (editIfAuthorized(__props.group)))
          }, _toDisplayString(_unref(websiteText)('global__edit').value), 1))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_5, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.group.rows, (row) => {
        return (_openBlock(), _createBlock(CartItem, {
          "campaign-percentage": getCampaignPercentage(row),
          key: row.id,
          row: row
        }, null, 8, ["campaign-percentage", "row"]))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("button", {
        class: "cart-group-item__remove-group-button",
        onClick: _cache[1] || (_cache[1] = ($event: any) => (removeProduct()))
      }, [
        _createTextVNode(_toDisplayString(_unref(websiteText)('global__remove').value) + " ", 1),
        _cache[3] || (_cache[3] = _createElementVNode("img", {
          src: _imports_0,
          alt: "trash icon"
        }, null, -1))
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("div", {
        class: "cart-group-item__pricing-summary",
        onClick: _cache[2] || (_cache[2] = ($event: any) => (isPriceExpanded.value = !isPriceExpanded.value))
      }, [
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("div", null, _toDisplayString(_unref(websiteText)('global__subtotal').value), 1),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", null, _toDisplayString(_unref(formatPrice)(groupPriceSum.value)), 1),
            _createVNode(ConfiguratorChevron, {
              class: "cart-group-item__subtotal-chevron",
              open: isPriceExpanded.value
            }, null, 8, ["open"])
          ])
        ])
      ]),
      (isPriceExpanded.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("div", null, _toDisplayString(_unref(websiteText)('global__label_products_menu').value), 1),
                _createElementVNode("div", null, _toDisplayString(_unref(formatPrice)(productPriceSum.value)), 1)
              ]),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mappedProducts.value, (row) => {
                return (_openBlock(), _createBlock(CartPriceItem, {
                  key: row.id,
                  item: row,
                  "is-decoration": false
                }, null, 8, ["item"]))
              }), 128))
            ]),
            (hasGiftCardAddons.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                  _createElementVNode("div", _hoisted_14, [
                    _createElementVNode("div", null, _toDisplayString(_unref(websiteText)('decoration__decorations').value), 1),
                    _createElementVNode("div", null, _toDisplayString(_unref(formatPrice)(giftcardAddonsPriceSum.value)), 1)
                  ]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mappedGiftCardAddons.value, (row) => {
                    return (_openBlock(), _createBlock(CartPriceItem, {
                      key: row.id,
                      item: row,
                      "is-decoration": true
                    }, null, 8, ["item"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            (hasPrintRow.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("div", null, _toDisplayString(_unref(websiteText)('decoration__decorations').value), 1),
                    _createElementVNode("div", null, _toDisplayString(_unref(formatPrice)(decorationPriceSum.value)), 1)
                  ]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mappedDecorations.value, (row) => {
                    return (_openBlock(), _createBlock(CartPriceItem, {
                      key: row.id,
                      item: row,
                      "is-decoration": true
                    }, null, 8, ["item"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_17, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(upsellItems.value, (upsellItem) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: upsellItem.text,
                  class: "cart-group-item__upsell-item"
                }, [
                  _createVNode(Checkbox, {
                    text: getUpsellItemText(upsellItem),
                    modelValue: upsellItem.isActive,
                    "onUpdate:modelValue": ($event: any) => ((upsellItem.isActive) = $event),
                    onChange: ($event: any) => (toggleUpsellItem(upsellItem))
                  }, null, 8, ["text", "modelValue", "onUpdate:modelValue", "onChange"]),
                  _createElementVNode("span", null, _toDisplayString(getUpsellItemPrice(upsellItem)), 1)
                ]))
              }), 128))
            ]),
            (hasGiftCardShipping.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                  _createElementVNode("div", _hoisted_19, [
                    _createElementVNode("div", null, _toDisplayString(_unref(websiteText)('checkout__freight').value), 1),
                    _createElementVNode("div", null, _toDisplayString(_unref(formatPrice)(giftcardShippingPriceSum.value)), 1)
                  ]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mappedGiftCardShipping.value, (row) => {
                    return (_openBlock(), _createBlock(CartPriceItem, {
                      key: row.id,
                      item: row,
                      "is-decoration": true
                    }, null, 8, ["item"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})